import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HOME } from "@/constants/testIds";
import BookingModal from "@/components/site/BookingModal";
import { CalendarClock, BookOpen, Sparkles, ScrollText, ArrowRight } from "lucide-react";

function Section({ eyebrow, title, children }) {
  return (
    <section className="border-t hairline pt-10 mt-14 first:mt-0 first:border-t-0 first:pt-0">
      <div className="mb-8">
        <div className="eyebrow">{eyebrow}</div>
        <h2 className="font-serif-ev text-3xl md:text-4xl mt-3 leading-tight">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/dashboard/summary"); setData(data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (!open) load(); }, [open]);

  if (!data) {
    return <div className="min-h-[60vh] grid place-items-center text-sm" style={{ color: "var(--ev-muted)" }}>Preparing your room…</div>;
  }

  return (
    <div data-testid={HOME.dashboardRoot} className="mx-auto max-w-[1200px] px-5 md:px-10 py-12 md:py-20">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end mb-12 md:mb-14">
        <div className="md:col-span-8">
          <div className="eyebrow">
            The Portal · {new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
          </div>
          <h1 className="font-serif-ev text-4xl sm:text-5xl md:text-6xl mt-4 leading-[1.02] tracking-tight">
            Welcome, <span className="italic" style={{ color: "var(--ev-blue-2)" }}>{(user?.name || "friend").split(" ")[0]}</span>.
          </h1>
        </div>
        <div className="md:col-span-4 md:text-right">
          <button onClick={() => setOpen(true)} className="ev-btn ev-btn-primary">
            Book a session <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 mb-14">
        {[
          { label: "Upcoming", value: data.upcoming.length, icon: CalendarClock },
          { label: "Completed", value: data.past.length, icon: ScrollText },
          { label: "Active paths", value: data.active_programs.length, icon: BookOpen },
          { label: "Prompts", value: data.prompts.length, icon: Sparkles },
        ].map((s) => (
          <div key={s.label} className="ev-card p-5 md:p-6">
            <s.icon className="w-4 h-4" style={{ color: "var(--ev-blue-2)" }} />
            <div className="font-serif-ev text-4xl mt-4">{s.value}</div>
            <div className="eyebrow mt-1 !text-[10px]">{s.label}</div>
          </div>
        ))}
      </div>

      <Section eyebrow="Sessions" title="Upcoming">
        {data.upcoming.length === 0 ? (
          <div className="text-sm" style={{ color: "var(--ev-muted)" }}>
            No sessions scheduled. When you&apos;re ready, book your next hour.
          </div>
        ) : (
          <div className="ev-card divide-y hairline overflow-hidden">
            {data.upcoming.map((b) => (
              <div key={b.id} className="grid grid-cols-12 gap-4 p-5 md:p-6">
                <div className="col-span-2 font-serif-ev text-2xl">
                  {new Date(b.date).toLocaleDateString(undefined, { day: "2-digit" })}
                  <span className="block text-xs uppercase tracking-widest mt-1" style={{ color: "var(--ev-muted)" }}>
                    {new Date(b.date).toLocaleDateString(undefined, { month: "short" })}
                  </span>
                </div>
                <div className="col-span-7">
                  <div className="font-serif-ev text-xl">{b.program_title || "Consultation"}</div>
                  <div className="text-xs mt-1 uppercase tracking-widest" style={{ color: "var(--ev-muted)" }}>{b.session_type}</div>
                  {b.intention && <div className="text-sm mt-2" style={{ color: "var(--ev-muted)" }}>Intention: {b.intention}</div>}
                </div>
                <div className="col-span-3 md:text-right text-sm" style={{ color: "var(--ev-muted)" }}>
                  {b.time}
                  <div className="eyebrow mt-1 !text-[10px]" style={{ color: "var(--ev-blue-2)" }}>{b.status}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section eyebrow="Journal" title="Prompts for this week">
        <ol className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {data.prompts.map((q, i) => (
            <li key={i} className="ev-card p-6">
              <div className="font-serif-ev text-3xl" style={{ color: "var(--ev-blue)" }}>{String(i + 1).padStart(2, "0")}</div>
              <div className="font-serif-ev text-xl mt-3 leading-snug">{q}</div>
            </li>
          ))}
        </ol>
      </Section>

      <Section eyebrow="Library" title="Resources">
        <div className="ev-card divide-y hairline overflow-hidden">
          {data.resources.map((r) => (
            <div key={r.id} className="grid grid-cols-12 gap-4 p-5 md:p-6 items-center">
              <div className="col-span-2 md:col-span-1 text-xs uppercase tracking-widest" style={{ color: "var(--ev-blue-2)" }}>{r.kind}</div>
              <div className="col-span-7 md:col-span-8 font-serif-ev text-lg md:text-xl">{r.title}</div>
              <div className="col-span-3 md:text-right text-sm" style={{ color: "var(--ev-muted)" }}>{r.read_time}</div>
            </div>
          ))}
        </div>
      </Section>

      <BookingModal open={open} onOpenChange={setOpen} program={null} />
    </div>
  );
}
